import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Modal, Button, Spinner, InputGroup, FormControl, Breadcrumb, Row, Col } from 'react-bootstrap'
import { setDraggedFile, showMessageModal } from 'Manage/actions'
import { getCookie, setCookie } from 'utils/general'
import { getType } from 'mime-lite'
import './DropBoxConnector.css'
import { DROPBOX_SCRIPT_SRC, DROPBOX_APP_KEY } from 'constants'
import CustomModal from 'components/CustomModal/CustomModal'
import { showDropboxModal } from 'Manage/actions'
import { useHistory } from 'react-router-dom'

import {
  imageExtensions,
  excelExtensions,
  docExtensions,
  ebookExtensions,
  pptExtensions,
  pdfExtensions,
  htmlExtensions,
  phpExtensions,
  zipExtensions,
  videoExtensions,
  audioExtensionsBrowserNative,
  codeExtensions,
  lottieExtensions,
  latexExtensions
} from 'helpers/file-type'
import FileItem from './components/FileItem'

const allowedExtensions = [
  ...imageExtensions,
  ...excelExtensions,
  ...docExtensions,
  ...ebookExtensions,
  ...pptExtensions,
  ...pdfExtensions,
  ...htmlExtensions,
  ...phpExtensions,
  ...zipExtensions,
  ...videoExtensions,
  ...audioExtensionsBrowserNative,
  ...codeExtensions,
  ...lottieExtensions,
  ...latexExtensions
]

const DropboxConnector = ({ onFileReady, showExplorerModal, onHide }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const [isDropboxLoading, setIsDropboxLoading] = useState(false)
  const [files, setFiles] = useState([])
  const [currentPath, setCurrentPath] = useState('')
  const [breadcrumbs, setBreadcrumbs] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const scriptLoadedRef = useRef(false)

  const loadDropboxScript = () => {
    if (scriptLoadedRef.current) return Promise.resolve()

    return new Promise((resolve, reject) => {
      if (window.Dropbox) {
        scriptLoadedRef.current = true
        resolve()
        return
      }

      const existingScript = document.querySelector(`script[src="${DROPBOX_SCRIPT_SRC}"]`)
      if (existingScript) {
        existingScript.addEventListener('load', () => {
          scriptLoadedRef.current = true
          resolve()
        })
        existingScript.addEventListener('error', () => reject(new Error('Failed to load Dropbox SDK')))
        return
      }

      const script = document.createElement('script')
      script.src = DROPBOX_SCRIPT_SRC
      script.async = true
      script.onload = () => {
        scriptLoadedRef.current = true
        resolve()
      }
      script.onerror = () => reject(new Error('Failed to load Dropbox SDK'))
      document.body.appendChild(script)
    })
  }

  const startDropboxOAuth = async (fileId = '') => {
    try {
      const { DropboxAuth } = window.Dropbox
      const dbxAuth = new DropboxAuth({ clientId: DROPBOX_APP_KEY })
      const redirectUri = `${window.location.origin}/manage/dropbox`
      const state = encodeURIComponent(JSON.stringify({ file_id: fileId }))
      const authUrl = await dbxAuth.getAuthenticationUrl(
        redirectUri,
        state,
        'code',
        'offline',
        undefined,
        undefined,
        true
      )
      sessionStorage.setItem('dropbox_code_verifier', dbxAuth.codeVerifier)
      window.location.href = authUrl
    } catch (error) {
      console.error('Error starting Dropbox OAuth:', error)
      dispatch(
        showMessageModal({
          title: 'Dropbox Error',
          message: 'Failed to initiate Dropbox authentication. Please try again.',
          error: true
        })
      )
    }
  }

  const finishDropboxOAuth = async (fileId = null) => {
    setIsDropboxLoading(true)
    try {
      const { DropboxAuth } = window.Dropbox
      const params = new URLSearchParams(window.location.search)
      const code = params.get('code')
      const codeVerifier = sessionStorage.getItem('dropbox_code_verifier')

      if (!code || !codeVerifier) throw new Error('Missing code or codeVerifier')

      const dbxAuth = new DropboxAuth({ clientId: DROPBOX_APP_KEY })
      dbxAuth.setCodeVerifier(codeVerifier)

      const response = await dbxAuth.getAccessTokenFromCode(`${window.location.origin}/manage/dropbox`, code)
      const token = response.result.access_token
      const refreshToken = response.result.refresh_token

      setCookie('dropbox_access_token', token, 7)
      setCookie('dropbox_refresh_token', refreshToken, 30)

      if (fileId) {
        const file = await fetchDropboxFile(token, fileId)
        if (file) {
          dispatch(setDraggedFile(file))
          onFileReady()
        }
      } else {
        fetchDropboxFiles(token, '')
        dispatch(showDropboxModal(true))
        history.push('/manage')
      }
    } catch (error) {
      console.error('Error finishing Dropbox OAuth:', error)
      dispatch(
        showMessageModal({
          title: 'Dropbox Error',
          message: 'Failed to complete Dropbox authentication. Please try again.',
          error: true
        })
      )
    } finally {
      setIsDropboxLoading(false)
    }
  }

  async function isTokenValid(accessToken) {
    try {
      const { Dropbox } = window.Dropbox
      const dbx = new Dropbox({ accessToken })
      await dbx.checkUser({ query: '' }) // simple ping
      return true // token is valid
    } catch (err) {
      if (err?.status === 401) {
        return false // token expired or revoked
      }
      throw err // some other error
    }
  }

  const refreshAccessToken = async () => {
    try {
      const refreshToken = getCookie('dropbox_refresh_token')
      if (!refreshToken) return null

      const { DropboxAuth } = window.Dropbox
      const dbxAuth = new DropboxAuth({ clientId: DROPBOX_APP_KEY })
      dbxAuth.setRefreshToken(refreshToken)

      const newToken = await dbxAuth.refreshAccessToken()
      const token = newToken.result.access_token
      setCookie('dropbox_access_token', token, 7)
      return token
    } catch (error) {
      console.error('Error refreshing token:', error)
      return null
    }
  }

  const getValidAccessToken = async () => {
    let token = getCookie('dropbox_access_token')
    let isValid = await isTokenValid(token)
    if (isValid) return token
    token = await refreshAccessToken()
    if (!token) startDropboxOAuth('')
    return token
  }

  const fetchDropboxFiles = async (accessToken, path = '') => {
    setIsDropboxLoading(true)
    try {
      const { Dropbox } = window.Dropbox
      const dbx = new Dropbox({ accessToken })
      const res = await dbx.filesListFolder({ path })
      setFiles(res.result.entries)
      setCurrentPath(path)
      const pathParts = path.split('/').filter(Boolean)
      setBreadcrumbs(pathParts)
    } catch (error) {
      console.error('Error fetching Dropbox files:', error)
    } finally {
      setIsDropboxLoading(false)
    }
  }

  const fetchDropboxFile = async (accessToken, fileId) => {
    try {
      const { Dropbox } = window.Dropbox
      const dbx = new Dropbox({ accessToken })
      const res = await dbx.filesDownload({ path: fileId })
      const blob = res.result.fileBlob
      const name = res.result.name
      const file = new File([blob], name, { type: getType(name) })
      file.path = name
      return file
    } catch (err) {
      console.error('Error fetching Dropbox file:', err)
      startDropboxOAuth(fileId)
    }
  }

  const handleFileClick = async (file) => {
    if (file['.tag'] === 'folder') {
      const token = await getValidAccessToken()
      fetchDropboxFiles(token, file.path_lower)
    } else {
      setIsDropboxLoading(true)
      const extension = file.name.split('.').pop().toLowerCase()
      if (!allowedExtensions.includes(extension)) {
        dispatch(
          showMessageModal({
            title: 'Unsupported File',
            message: 'This file type is not supported. Please select another file.',
            error: true
          })
        )
        return
      }

      const token = await getValidAccessToken()
      if (token) {
        const selectedFile = await fetchDropboxFile(token, file.path_lower)
        if (selectedFile) {
          dispatch(setDraggedFile(selectedFile))
          onFileReady()
          onHide()
        }
      }
      setIsDropboxLoading(false)
    }
  }

  const handleBreadcrumbClick = (index) => {
    const newPath = '/' + breadcrumbs.slice(0, index + 1).join('/')
    fetchDropboxFiles(getCookie('dropbox_access_token'), newPath)
  }

  useEffect(() => {
    const handleRedirect = async () => {
      if (window.location.pathname !== '/manage/dropbox') return

      await loadDropboxScript()

      const params = new URLSearchParams(window.location.search)
      const code = params.get('code')
      let fileId = params.get('file_id')

      if (!fileId && params.get('state')) {
        try {
          const stateObj = JSON.parse(decodeURIComponent(params.get('state')))
          fileId = stateObj.file_id
        } catch (e) {
          console.error('Failed to parse Dropbox state:', e)
        }
      }

      if (fileId) {
        setIsDropboxLoading(true)
        const token = await getValidAccessToken()
        if (token) {
          const selectedFile = await fetchDropboxFile(token, fileId)
          if (selectedFile) {
            dispatch(setDraggedFile(selectedFile))
            onFileReady()
            onHide()
          }
        }
      }

      if (code) {
        await finishDropboxOAuth(fileId)
      }
    }

    handleRedirect()
  }, [])

  useEffect(() => {
    const initExplorer = async () => {
      if (!showExplorerModal) return
      await loadDropboxScript()

      const token = await getValidAccessToken()
      if (token) fetchDropboxFiles(token, '')
    }

    initExplorer()
  }, [showExplorerModal])

  const filteredFiles = files.filter((file) => file.name.toLowerCase().includes(searchQuery.toLowerCase()))

  const tokenAvailable = !!getCookie('dropbox_access_token') || !!getCookie('dropbox_refresh_token')

  return (
    <CustomModal
      backdrop="static"
      size="lg"
      show={showExplorerModal}
      aria-labelledby="contained-modal-title-vcenter"
      centered
      enforceFocus={false}
      onHide={onHide}
      className="dropbox-connector"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <h2 className="bold color-purple-2 font-22">Your Dropbox Files</h2>{' '}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isDropboxLoading ? (
          <div className="text-center">
            <Spinner animation="border" />
          </div>
        ) : !tokenAvailable ? (
          <div className="text-center">
            <p>Import your files from Dropbox</p>
            <Button variant="primary" onClick={() => startDropboxOAuth('')}>
              Connect to Dropbox
            </Button>
          </div>
        ) : (
          <>
            <Breadcrumb>
              <Breadcrumb.Item
                onClick={() => fetchDropboxFiles(getCookie('dropbox_access_token'), '')}
                className="text-underline-none color-purple-22"
              >
                Home
              </Breadcrumb.Item>
              {breadcrumbs.map((crumb, idx) => (
                <Breadcrumb.Item
                  key={idx}
                  onClick={() => handleBreadcrumbClick(idx)}
                  className="text-underline-none color-purple-22"
                >
                  {crumb}
                </Breadcrumb.Item>
              ))}
            </Breadcrumb>

            <InputGroup className="mb-3">
              <FormControl
                placeholder="Search files..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </InputGroup>

            <Row>
              {filteredFiles.map((file) => (
                <Col key={file.id} xs={6} sm={4} md={3} className="mb-3">
                  <FileItem
                    file={file}
                    accessToken={getCookie('dropbox_access_token')}
                    onOpen={() => handleFileClick(file)}
                  />
                </Col>
              ))}
            </Row>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="mt-3">
          <Button className="float-right" variant="outline-primary" onClick={onHide}>
            Close
          </Button>
        </div>
      </Modal.Footer>
    </CustomModal>
  )
}

export default DropboxConnector
